import { createInvitation } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { CreateInvitationRequest } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';

import { translateApiError } from '../../../../shared/errors/translateApiError';
import { Select } from './Select';

type ManageableRole = 'supervisor' | 'operator';

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (email: string) => void;
}

export function InviteDialog({ open, onOpenChange, onCreated }: InviteDialogProps) {
  const { t } = useTranslation('users');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ManageableRole>('operator');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setRole('operator');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateInvitationRequest = { email, role };
      await createInvitation(input);
      onCreated(email);
      onOpenChange(false);
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('inviteDialog.error') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('inviteDialog.title')}</DialogTitle>
          <DialogDescription>{t('inviteDialog.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">{t('inviteDialog.emailLabel')}</Label>
            <Input
              id="invite-email"
              type="email"
              required
              autoFocus
              placeholder={t('inviteDialog.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-role">{t('inviteDialog.roleLabel')}</Label>
            <Select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as ManageableRole)}
              disabled={submitting}
              className="w-full"
            >
              <option value="supervisor">{t('roles.supervisor')}</option>
              <option value="operator">{t('roles.operator')}</option>
            </Select>
          </div>

          {error && (
            <p className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('inviteDialog.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? t('inviteDialog.submitting') : t('inviteDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
